import { z } from 'zod';

import { mapOrderToWriteOrder } from '#api/adapters/orderAdapter.ts';
import type { ApiWriteTourDuplicate } from '#api/model/types.ts';
import type { Order } from '#domain/Order.ts';
import type { StopType } from '#domain/Stop.ts';
import type { Tour } from '#domain/Tour.ts';
import type { TourStop } from '#domain/TourStop.ts';
import { RIODateTime } from '#lib/RIODateTime.ts';

export const TourDuplicateFormValidation = z
    .object({
        keepTourName: z.boolean(),
        tourName: z.string(),
        note: z.string().optional(),
        setDateToToday: z.boolean(),
        orderDate: z.string(),
        keepAllTourOrders: z.boolean(),
        selectedOrderIds: z.array(z.string()),
        keepSelectedVehicle: z.boolean(),
        vehicleId: z.string(),
        duplicateDriverComments: z.boolean(),
        notificationsEnabled: z.boolean(),
    })
    .superRefine((value, ctx) => {
        if (!value.keepTourName && value.tourName.trim().length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['tourName'],
                message: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.enter-tour-name',
            });
        }

        if (!value.setDateToToday && value.orderDate.trim().length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['orderDate'],
                message: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.enter-date',
            });
        }

        if (!value.keepAllTourOrders && value.selectedOrderIds.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['selectedOrderIds'],
                message: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.select-order',
            });
        }

        if (!value.keepSelectedVehicle && value.vehicleId.trim().length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['vehicleId'],
                message: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.select-vehicle',
            });
        }
    });

export type TourDuplicateFormValues = z.infer<typeof TourDuplicateFormValidation>;

const getTodayDate = () => RIODateTime.Now().toPredefinedFormat(RIODateTime.PREDEFINED_FORMATS.DAY_ISO);

const getLocalTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Berlin';

const toDuplicateStopType = (type: StopType): 'load' | 'unload' | 'execute' => {
    if (type === 'load' || type === 'unload' || type === 'execute') {
        return type;
    }
    throw new Error(`Unsupported stop type for tour duplication: ${type}`);
};

export const getInitialValues = (selectedTour: Tour, orderIds: string[]): TourDuplicateFormValues => ({
    keepTourName: true,
    tourName: selectedTour.searchName,
    note: selectedTour.note,
    setDateToToday: true,
    orderDate: getTodayDate(),
    keepAllTourOrders: true,
    selectedOrderIds: orderIds,
    keepSelectedVehicle: true,
    vehicleId: selectedTour.truck.id,
    duplicateDriverComments: true,
    notificationsEnabled: true,
});

export const toDuplicateTourPayload = (
    values: TourDuplicateFormValues,
    sortedStops: TourStop[],
    selectedOrders: Order[]
): ApiWriteTourDuplicate => {
    const selectedDate = values.setDateToToday ? getTodayDate() : values.orderDate;

    return {
        tour: {
            search_name: values.tourName.trim(),
            note: values.duplicateDriverComments ? values.note : undefined,
            truck_id: values.vehicleId,
            date: selectedDate,
            time_zone: getLocalTimeZone(),
            stops: sortedStops.map((stop, index) => ({
                order_id: stop.orderId,
                type: toDuplicateStopType(stop.type),
                index,
            })),
        },
        orders: selectedOrders.map(order => {
            const writeOrder = mapOrderToWriteOrder(order);
            if (writeOrder.type === 'service') {
                return {
                    temp_id: order.id,
                    ...writeOrder,
                };
            }
            return {
                temp_id: order.id,
                ...writeOrder,
                comments: values.duplicateDriverComments ? writeOrder.comments : undefined,
            };
        }),
    };
};
